import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import {
  PORTFOLIO_HOLDING_BY_ID,
  PORTFOLIO_REFRESH_XIRR_BY_ID,
} from "utils/apiRoutes";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import useGenerateQueryParams from "./useGenerateQueryParams";
import { useFetchQuery } from "./useQueries";

const usePortfolioHoldingDetails = ({
  portfolioExtendedViewTabs,
}: {
  portfolioExtendedViewTabs: number;
}) => {
  const { id } = useParams<string>();

  const columnsPersist = useAppSelector(
    (state) => state?.table?.columnsPersist,
  );
  const userData = useAppSelector((state) => state?.user?.userData);

  const navigate = useNavigate();

  const [tableColumnModalOpen, setTableColumnModalOpen] = useState(false);

  const [filterModal, setFilterModal] = useState(false);

  const [isXirrRefresh, setIsXirrRefresh] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [allHoldings, setAllHoldings] = useState([]);

  const handleReturnInstType = (val: number) => {
    switch (val) {
      case 1:
        return JSON.stringify([]);
      case 2:
        return JSON.stringify(["EQ"]);
      case 3:
        return JSON.stringify(["MF"]);
      case 4:
        return JSON.stringify(["ETF"]);
      case 5:
        return JSON.stringify(["CE"]);
      case 6:
        return JSON.stringify(["PEQ"]);
      case 7:
        return JSON.stringify(["FD"]);
      case 8:
        return JSON.stringify(["AIF"]);
      case 9:
        return JSON.stringify(["FUTURES", "OPTIONS"]);

      default:
        return JSON.stringify([
          "NCE",
          "MF",
          "CE",
          "PEQ",
          "FD",
          "AIF",
          "FUTURES",
          "OPTIONS",
        ]);
    }
  };

  const requestBody = useGenerateQueryParams("portfolioHoldingsTable", {
    inst_type: handleReturnInstType(portfolioExtendedViewTabs),
  });

  // ============================================================
  const { isFetching: xirrLoading } = useFetchQuery({
    route: PORTFOLIO_REFRESH_XIRR_BY_ID(id),
    key: ["GET_PORTFOLIO_XIRR"],
    enabled: isXirrRefresh,
    success: () => {
      refetchQuery("GET_PORTFOLIO_HOLDINGS_CARD");
      setIsXirrRefresh(false);
    },
  });

  // ============================================================
  const { isFetching: isLoading, data: holdingsData } = useFetchQuery({
    key: [QueryKeys.HOLDINGS_LIST, id, requestBody],
    route: PORTFOLIO_HOLDING_BY_ID(+id),
    requestBody,
    success: (response: any) => {
      if (response?.data?.data.length > 0) {
        setAllHoldings([
          ...response?.data?.data,
          {
            unrealisedGain: response?.data?.unrealisedGain,
            realisedGain: response?.data?.realisedGain,
            otherGains: response?.data?.otherGains,
            quantity: response?.data?.quantity,
            rawQuantity: response?.data?.quantity,
            isValid: true,
            investedAmount: response?.data?.investedAmount,
            totalNetInvestment: response?.data?.totalNetInvestment,
            currentAmount: response?.data?.currentAmount,
            totalAmount: response?.data?.totalAmount,
            todaysGain: response?.data?.todaysGain,
            todaysGainPercentage: response?.data?.todaysGainPercentage,
            lastTradedPrice: response?.data?.lastTradedPrice,
            avgBuyPrice: response?.data?.avgBuyPrice,
            cmp: response?.data?.cmp,
            yesterdayValue: response?.data?.yesterdayValue,
            unrealisedGainPercentage: response?.data?.unrealisedGainPercentage,
            absoluteReturn: response?.data?.absoluteReturn,
            ltHoldingsCount: response?.data?.ltHoldingsCount,
            instType: " ",
            instSubType: " ",
            folioNo: response?.data?.folioNo,
            weightage: response?.data?.weightage,
            id: 100000,
            isin: " ",
            gcode: " ",
            name: "",
            fullName: " ",
            cap: " ",
            sector: " ",
            bse: " ",
            nse: "",
            lastPrice: response?.data?.lastPrice,
            pastPrice: response?.data?.pastPrice,
          },
        ]);
      } else {
        setAllHoldings(response.data.data);
      }
    },
  });

  // ============================================================
  const loading = xirrLoading || isLoading;
  // ============================================================
  return {
    id,
    loading,
    navigate,
    allHoldings,
    setSearchText,
    searchText,
    columnsPersist,
    tableColumnModalOpen,
    setTableColumnModalOpen,
    filterModal,
    setFilterModal,
    holdingsData,
    userData,
  };
};

export default usePortfolioHoldingDetails;
